import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageContainer, Button, Input, Select, Skeleton, ErrorBoundary, Alert, useToast } from '@bbpos/ui';
import { GLASS_FORM } from '@/constants/design-tokens';
import { useCreateRfq, useUpdateRfq, useRfq } from '@/features/rfqs';
import type { RfqItemPayload } from '@/features/rfqs';
import { usePurchaseOrder } from '@/features/purchase-orders';
import type { POUnit } from '@/features/purchase-orders';
import { suppliersApi } from '@/features/suppliers';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { locationService } from '@/services/location.service';
import { productService } from '@/services/product.service';
import { ingredientService } from '@/services/ingredient.service';
import { buildProductSearchParams } from '../purchase-orders/purchase-order-products';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon, PlusIcon, TrashIcon } from '../purchase-orders/icons';

const UNIT_OPTIONS: { value: POUnit; label: string }[] = [
  { value: 'PCS', label: 'Pieces' },
  { value: 'KG', label: 'Kilograms' },
  { value: 'G', label: 'Grams' },
  { value: 'L', label: 'Litres' },
  { value: 'ML', label: 'Millilitres' },
  { value: 'DOZEN', label: 'Dozen' },
  { value: 'BOX', label: 'Box' },
  { value: 'CARTON', label: 'Carton' },
];

const STEPS = [
  { id: 'details', label: 'Details' },
  { id: 'items', label: 'Items' },
  { id: 'suppliers', label: 'Suppliers' },
  { id: 'review', label: 'Review & Save' },
];

interface ItemRow {
  key: string;
  item_id: string;
  item_name: string;
  quantity: string;
  unit: POUnit;
}

function generateKey() {
  return Math.random().toString(36).slice(2, 10);
}
function blankRow(overrides: Partial<ItemRow> = {}): ItemRow {
  return { key: generateKey(), item_id: '', item_name: '', quantity: '', unit: 'PCS', ...overrides };
}

function RfqCreateContent() {
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = Boolean(editId);
  const navigate = useNavigate();
  const { toast } = useToast();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);
  const businessType = useAuthStore((s) => s.profile?.tenant?.business_type);
  const isRestaurant = businessType === 'RESTAURANT';
  const createMutation = useCreateRfq();
  const updateMutation = useUpdateRfq();
  const { data: existing, isLoading: loadingExisting } = useRfq(editId);
  // "Create RFQ from PO": prefill lines / location / supplier from an existing PO.
  const [searchParams] = useSearchParams();
  const fromPoId = !isEdit ? searchParams.get('from_po_id') ?? undefined : undefined;
  const { data: sourcePo, isLoading: loadingSourcePo } = usePurchaseOrder(fromPoId);

  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [locationId, setLocationId] = useState(selectedLocationId ?? '');
  const [expectedDate, setExpectedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<ItemRow[]>([blankRow()]);
  const [supplierIds, setSupplierIds] = useState<string[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [hydrated, setHydrated] = useState(!isEdit && !fromPoId);

  // Edit mode: hydrate once from the loaded DRAFT.
  useEffect(() => {
    if (!isEdit || hydrated || !existing) return;
    if (existing.status !== 'DRAFT') {
      toast({ title: 'Only draft RFQs can be edited', variant: 'error' });
      navigate(`/inventory/rfqs/${existing.id}`);
      return;
    }
    setLocationId(existing.location_id);
    setExpectedDate(existing.expected_delivery_date ? existing.expected_delivery_date.slice(0, 10) : '');
    setNotes(existing.notes ?? '');
    setItems(existing.items.map((i) => blankRow({
      item_id: (i.product_id ?? i.ingredient_id) as string,
      item_name: i.item_name,
      quantity: String(i.quantity),
      unit: i.unit,
    })));
    setSupplierIds(existing.suppliers.map((s) => s.supplier_id));
    setHydrated(true);
  }, [isEdit, hydrated, existing, navigate, toast]);

  // From-PO mode: copy the PO's lines once. Expected date is left blank (the
  // PO's is stale); the PO's supplier is pre-ticked so it can be re-quoted
  // alongside others. Auto-generated POs have no supplier → nothing ticked.
  useEffect(() => {
    if (!fromPoId || hydrated || !sourcePo) return;
    setLocationId(sourcePo.location_id);
    setItems(sourcePo.items.map((i) => blankRow({
      item_id: (i.product_id ?? (i as { ingredient_id?: string | null }).ingredient_id ?? '') as string,
      item_name: i.product_name,
      quantity: String(i.quantity),
      unit: i.unit,
    })));
    if (sourcePo.supplier_id) setSupplierIds([sourcePo.supplier_id]);
    setHydrated(true);
  }, [fromPoId, hydrated, sourcePo]);

  const { data: locationsData } = useQuery({
    queryKey: ['locations-list'],
    queryFn: () => locationService.list({ limit: 100, is_active: 'true' }),
    staleTime: 60_000,
  });
  const locations = locationsData?.data ?? [];

  const { data: productsData } = useQuery({
    queryKey: ['products-search', productSearch],
    queryFn: () => productService.list(buildProductSearchParams(productSearch)),
    staleTime: 15_000,
    enabled: !isRestaurant,
  });
  const { data: ingredientsData } = useQuery({
    queryKey: ['ingredients-search'],
    queryFn: () => ingredientService.listIngredients({ is_active: 'true', limit: 200 }),
    staleTime: 15_000,
    enabled: isRestaurant,
  });
  const catalog = useMemo(() => {
    if (isRestaurant) return (ingredientsData?.data ?? []).map((g) => ({ id: g.id, name: g.name, label: g.name }));
    return (productsData?.data ?? []).map((p) => ({ id: p.id, name: p.name, label: `${p.name} (${p.sku})` }));
  }, [isRestaurant, ingredientsData, productsData]);

  const selectedItemIds = useMemo(() => new Set(items.map((i) => i.item_id).filter(Boolean)), [items]);
  const itemOptions = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    return catalog
      .filter((c) => !selectedItemIds.has(c.id))
      .filter((c) => !isRestaurant || !q || c.name.toLowerCase().includes(q))
      .map((c) => ({ value: c.id, label: c.label }));
  }, [catalog, selectedItemIds, isRestaurant, productSearch]);

  // Active suppliers for the multi-select. Loaded once (≤200) — the picker filters client-side.
  const { data: suppliersData, isLoading: suppliersLoading } = useQuery({
    queryKey: ['rfq-suppliers-all'],
    queryFn: () => suppliersApi.list({ is_active: true, limit: 200 }),
    staleTime: 60_000,
  });
  const suppliers = suppliersData?.data ?? [];
  const supplierOptions = useMemo(
    () => suppliers.map((s) => ({ value: s.id, label: s.email ? `${s.name} — ${s.email}` : `${s.name} (no email)` })),
    [suppliers],
  );
  const selectedSuppliers = useMemo(() => suppliers.filter((s) => supplierIds.includes(s.id)), [suppliers, supplierIds]);
  const suppliersWithoutEmail = selectedSuppliers.filter((s) => !s.email);

  const validItems = useMemo(() => items.filter((i) => i.item_id && parseFloat(i.quantity) > 0), [items]);

  function validateStep(step: number): boolean {
    const errs: Record<string, string> = {};
    if (step === 0 && !locationId) errs.locationId = 'Location is required';
    if (step === 1) {
      const chosen = items.filter((i) => i.item_id);
      if (chosen.length === 0) errs.items = 'Add at least one item';
      for (const it of chosen) {
        if (!(parseFloat(it.quantity) > 0)) errs[`item_${it.key}_qty`] = 'Quantity must be > 0';
      }
    }
    if (step === 2 && supplierIds.length === 0) errs.suppliers = 'Select at least one supplier';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }
  function handleNext() { if (validateStep(currentStep)) setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1)); }
  function handleBack() { setErrors({}); setCurrentStep((s) => Math.max(s - 1, 0)); }

  function updateItem(key: string, field: keyof ItemRow, value: string) {
    setItems((prev) => prev.map((i) => {
      if (i.key !== key) return i;
      const updated = { ...i, [field]: value };
      if (field === 'item_id') {
        const picked = catalog.find((c) => c.id === value);
        if (picked) updated.item_name = picked.name;
      }
      return updated;
    }));
  }

  const handleSubmit = useCallback(() => {
    const payload = {
      location_id: locationId,
      expected_delivery_date: expectedDate || undefined,
      notes: notes.trim() || undefined,
      items: validItems.map((i): RfqItemPayload => ({
        ...(isRestaurant ? { ingredient_id: i.item_id } : { product_id: i.item_id }),
        quantity: parseFloat(i.quantity),
        unit: i.unit,
      })),
      supplier_ids: supplierIds,
    };
    const onError = (err: Error) => toast({ title: err.message, variant: 'error' });
    if (isEdit && editId) {
      updateMutation.mutate({ id: editId, payload }, {
        onSuccess: (rfq) => { toast({ title: `${rfq.rfq_number} updated`, variant: 'success' }); navigate(`/inventory/rfqs/${rfq.id}`); },
        onError,
      });
    } else {
      createMutation.mutate(payload, {
        onSuccess: (rfq) => { toast({ title: `${rfq.rfq_number} created`, variant: 'success' }); navigate(`/inventory/rfqs/${rfq.id}`); },
        onError,
      });
    }
  }, [locationId, expectedDate, notes, validItems, isRestaurant, supplierIds, isEdit, editId, createMutation, updateMutation, navigate, toast]);

  const saving = createMutation.isPending || updateMutation.isPending;

  if ((isEdit && (loadingExisting || !hydrated)) || (fromPoId && (loadingSourcePo || !hydrated))) {
    return <Skeleton variant="rect" height={400} className="rounded-xl" />;
  }

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-teal-600 via-teal-500 to-brand-600 p-6 text-white shadow-[0_24px_60px_-24px_rgba(13,148,136,0.55)] mb-6 animate-fade-in">
        <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="relative">
          <h1 className="text-2xl font-bold tracking-tight text-white">{isEdit ? `Edit ${existing?.rfq_number ?? 'RFQ'}` : 'Create RFQ'}</h1>
          <p className="text-teal-100 mt-1">List what you need, pick the suppliers to ask, then send it out for quotes</p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-6 animate-slide-up">
        {STEPS.map((step, idx) => (
          <div key={step.id} className="flex items-center gap-2">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold transition-all duration-200 ${idx <= currentStep ? 'bg-teal-600 text-white shadow-sm' : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-500'}`}>{idx + 1}</div>
            <span className={`text-sm font-medium ${idx <= currentStep ? 'text-neutral-900 dark:text-white' : 'text-neutral-400'}`}>{step.label}</span>
            {idx < STEPS.length - 1 && <div className={`w-10 h-0.5 ${idx < currentStep ? 'bg-teal-500' : 'bg-neutral-200 dark:bg-neutral-700'}`} />}
          </div>
        ))}
      </div>

      <div className={`${GLASS_FORM} p-6 animate-slide-up`}>
        {sourcePo && currentStep < 3 && (
          <div className="mb-5">
            <Alert
              variant="info"
              title={`Copied from ${sourcePo.po_number}`}
              description={`${sourcePo.items.length} line(s)${sourcePo.supplier_id ? ` and ${sourcePo.supplier_name}` : ''} carried over — edit quantities, add items or more suppliers before sending.`}
            />
          </div>
        )}
        {currentStep === 0 && (
          <div className="space-y-5 max-w-2xl">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Deliver to <span className="text-danger-500">*</span></label>
              <Select options={locations.map((l) => ({ value: l.id, label: l.name }))} value={locationId}
                onChange={(v) => setLocationId(typeof v === 'string' ? v : '')} placeholder="Select location" error={errors.locationId} />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Required by</label>
              <Input type="date" value={expectedDate} onChange={(v) => setExpectedDate(v)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Notes to suppliers</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={1000}
                placeholder="e.g. Please quote landed cost incl. freight; validity 15 days"
                className="w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-2 text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-colors" />
            </div>
          </div>
        )}

        {currentStep === 1 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">Items to quote</h3>
              <div className="w-56">
                <Input placeholder={isRestaurant ? 'Search ingredients...' : 'Search products...'} value={productSearch} onChange={(v) => setProductSearch(v)} />
              </div>
            </div>
            {errors.items && <p className="text-xs text-danger-600">{errors.items}</p>}
            <div className="overflow-visible">
              <table className="w-full table-fixed text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 dark:border-neutral-700">
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">{isRestaurant ? 'Ingredient' : 'Product'}</th>
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide w-32">Quantity</th>
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide w-40">Unit</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.key} className="border-b border-neutral-100 dark:border-neutral-800/50 last:border-0">
                      <td className="py-2 pr-3">
                        <Select
                          variant="searchable"
                          options={[...(item.item_id ? [{ value: item.item_id, label: item.item_name || item.item_id }] : []), ...itemOptions]}
                          value={item.item_id}
                          onChange={(v) => updateItem(item.key, 'item_id', typeof v === 'string' ? v : '')}
                          placeholder={isRestaurant ? 'Select ingredient' : 'Select product'}
                        />
                      </td>
                      <td className="py-2 pr-3">
                        <Input type="number" value={item.quantity} onChange={(v) => updateItem(item.key, 'quantity', v)} placeholder="0" />
                        {errors[`item_${item.key}_qty`] && <p className="text-xs text-danger-600 mt-0.5">{errors[`item_${item.key}_qty`]}</p>}
                      </td>
                      <td className="py-2 pr-3">
                        <Select options={UNIT_OPTIONS} value={item.unit} onChange={(v) => updateItem(item.key, 'unit', typeof v === 'string' ? v : 'PCS')} />
                      </td>
                      <td className="py-2">
                        <button type="button" onClick={() => setItems((prev) => (prev.length <= 1 ? prev : prev.filter((i) => i.key !== item.key)))}
                          disabled={items.length <= 1} title="Remove item"
                          className="p-1.5 rounded-lg text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button variant="secondary" onClick={() => setItems((prev) => [...prev, blankRow()])} className="mt-2">
              <PlusIcon className="h-4 w-4 mr-1.5" />
              Add Item
            </Button>
          </div>
        )}

        {currentStep === 2 && (
          <div className="space-y-4 max-w-3xl">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Suppliers to ask <span className="text-danger-500">*</span></label>
              <Select
                variant="multi-select"
                options={supplierOptions}
                value={supplierIds}
                onChange={(v) => setSupplierIds(Array.isArray(v) ? (v as string[]) : [])}
                placeholder={suppliersLoading ? 'Loading suppliers…' : 'Search and pick suppliers'}
                error={errors.suppliers}
              />
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                Only active suppliers from Inventory → Suppliers are listed. Add new suppliers there first.
              </p>
            </div>
            {suppliersWithoutEmail.length > 0 && (
              <div className="rounded-lg border border-warning-200 bg-warning-50 dark:bg-warning-900/20 dark:border-warning-800 px-3 py-2 text-sm text-warning-800 dark:text-warning-200">
                No email on file for {suppliersWithoutEmail.map((s) => s.name).join(', ')} — they will be skipped when sending unless you enter an address in the Send dialog.
              </div>
            )}
            {selectedSuppliers.length > 0 && (
              <ul className="divide-y divide-neutral-100 dark:divide-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
                {selectedSuppliers.map((s) => (
                  <li key={s.id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="font-medium text-neutral-800 dark:text-neutral-200">{s.name}</span>
                    <span className="text-neutral-500 dark:text-neutral-400 tabular-nums">{s.email ?? '—'}{s.phone ? ` · ${s.phone}` : ''}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {currentStep === 3 && (
          <div className="space-y-6">
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div><dt className="text-xs text-neutral-500 uppercase tracking-wide mb-0.5">Deliver to</dt><dd className="text-neutral-800 dark:text-neutral-200">{locations.find((l) => l.id === locationId)?.name ?? locationId}</dd></div>
              <div><dt className="text-xs text-neutral-500 uppercase tracking-wide mb-0.5">Required by</dt><dd className="text-neutral-800 dark:text-neutral-200 tabular-nums">{expectedDate || '—'}</dd></div>
              <div><dt className="text-xs text-neutral-500 uppercase tracking-wide mb-0.5">Suppliers</dt><dd className="text-neutral-800 dark:text-neutral-200">{selectedSuppliers.map((s) => s.name).join(', ')}</dd></div>
              {notes && <div className="sm:col-span-3"><dt className="text-xs text-neutral-500 uppercase tracking-wide mb-0.5">Notes</dt><dd className="text-neutral-800 dark:text-neutral-200">{notes}</dd></div>}
            </dl>
            <div>
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">Items ({validItems.length})</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 dark:border-neutral-700">
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Item</th>
                    <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Qty</th>
                    <th className="text-left py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide">Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {validItems.map((i) => (
                    <tr key={i.key} className="border-b border-neutral-100 dark:border-neutral-800/50 last:border-0">
                      <td className="py-2.5 pr-3 font-medium text-neutral-800 dark:text-neutral-200">{i.item_name}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums text-neutral-800 dark:text-neutral-200">{parseFloat(i.quantity)}</td>
                      <td className="py-2.5 text-neutral-500 dark:text-neutral-400">{i.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-8 pt-6 border-t border-neutral-200 dark:border-neutral-700">
          {currentStep === 0 ? (
            <Button variant="secondary" onClick={() => navigate(isEdit && editId ? `/inventory/rfqs/${editId}` : '/inventory/rfqs')}>
              <ArrowLeftIcon className="h-4 w-4 mr-1.5" />Cancel
            </Button>
          ) : (
            <Button variant="secondary" onClick={handleBack}><ArrowLeftIcon className="h-4 w-4 mr-1.5" />Back</Button>
          )}
          {currentStep < STEPS.length - 1 ? (
            <Button variant="primary" onClick={handleNext}>Next</Button>
          ) : (
            <Button variant="primary" onClick={handleSubmit} disabled={saving || validItems.length === 0 || supplierIds.length === 0}>
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create RFQ'}
            </Button>
          )}
        </div>
      </div>
    </>
  );
}

export function RfqCreatePage() {
  const { id } = useParams<{ id: string }>();
  return (
    <PageContainer
      title={id ? 'Edit RFQ' : 'Create RFQ'}
      breadcrumbs={[{ label: 'Inventory', href: '/inventory' }, { label: 'RFQs', href: '/inventory/rfqs' }, { label: id ? 'Edit' : 'New' }]}
    >
      <ErrorBoundary>
        <RfqCreateContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
